<template>
  <div class="rich-text relative">
    <perfect-scrollbar class="p-2">
      <slot name="prefix"/>
      <div :id="textAreaId"
           class="text-base"
      />
      <slot name="suffix"/>
    </perfect-scrollbar>

    <rich-input-toolbar v-if="!isDisabled"
                        :id="toolbarId"
                        :formatting="formatting"
                        :cursor-position="cursorPosition"
                        :text-editor-id="textAreaId"
                        :get-selected-text-editor="getSelectedText"
                        @apply-format="applyFormat"
                        @insert-emoji="insertEmoji"
                        @insert-link="insertLink"
                        @remove-link="removeLink"
                        @edit-link="editLink"
    />
  </div>
</template>

<script>
import Quill from 'quill/quill'
import RichInputToolbar from '@/components/base-components/rich-input/RichInputToolbar.vue'
import 'quill/dist/quill.core.css'
import { REGEXES } from '@/utils/key-regex.constants'
import randomUtils from '@/utils/random.utils'
import { PerfectScrollbar } from 'vue2-perfect-scrollbar'
import includes from 'ramda/src/includes'
import equals from 'ramda/src/equals'
import path from 'ramda/src/path'
import isNil from 'ramda/src/isNil'

export default {
  components: {
    RichInputToolbar,
    PerfectScrollbar
  },

  name: 'RichInput',

  data() {
    return {
      editor: {},
      formatting: {
        bold: false,
        italic: false,
        list: 'bullet',
        align: 'left',
        link: ''
      },
      cursorPosition: { left: 0, right: 0, top: 0, height: 0 },
      textAreaId: null,
      toolbarId: null
    }
  },

  props: {
    value: {
      type: String,
      default: ''
    },

    isDisabled: {
      type: Boolean,
      default: false
    },

    autofocus: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    config() {
      return {
        modules: {
          toolbar: this.isDisabled ? null : `#${this.toolbarId}`
        }
      }
    },

    currentText() {
      return this.editor.getText()
    }
  },

  methods: {
    getSelectedText() {
      const selection = this.editor.getSelection()
      if (selection && selection.length > 0) {
        return this.editor.getText(selection.index, selection.length)
      }
      return ''
    },

    initEditorIds() {
      const id = randomUtils.randomString(5, 'n')
      this.textAreaId = `text-area-${id}`
      this.toolbarId = `toolbar-${id}`
    },

    initEditor() {
      const alignStyles = Quill.import('attributors/style/align')
      Quill.register(alignStyles, true)

      this.editor = new Quill(`#${this.textAreaId}`, this.config)
      this.editor.root.innerHTML = this.value
      this.editor.enable(!this.isDisabled)
      if (this.autofocus) {
        this.editor.focus()
      }
    },

    initEvents() {
      this.editor.on('editor-change', () => this.onEditorUpdate())
    },

    onEditorUpdate() {
      const { index, length } = this.getSelection()
      this.formatting = this.editor.getFormat(index, length)
      this.cursorPosition = this.editor.getBounds(index, length)
      const contentUpdate = this.editor.root.innerHTML.replaceAll('<p><br></p>', '<br/>')
      this.$emit('input', contentUpdate)
    },

    applyFormat(payload) {
      if (includes(payload.prop, ['bold', 'italic'])) {
        this.editor.format(payload.prop, !this.formatting[payload.prop])
      } else {
        this.editor.format(payload.prop, this.resolveFormatValue(payload.val))
      }
    },

    resolveFormatValue(value) {
      return equals(value, 'left') ? false : value
    },

    insertEmoji(emoji) {
      const { index } = this.getSelection(true)
      this.editor.insertText(index, emoji.data)
    },

    handleImageInsertion(index, url) {
      this.editor.insertEmbed(index, 'image', url)
      this.editor.setSelection(index + 1, 0)
    },

    handleYouTubeInsertion(index, url) {
      this.editor.insertEmbed(
        index,
        'video',
        `https://www.youtube.com/embed/${this.getYoutubeId(url)}`
      )
      this.editor.setSelection(index + 1, 0)
    },

    findLinkBlotByUrl(urlToFind) {
      if (!urlToFind) return null
      const LinkBlot = Quill.import('formats/link')
      const blots = this.editor.scroll.descendants(LinkBlot)
      for (const blot of blots) {
        if (equals(blot.formats().link, urlToFind)) {
          return blot
        }
      }
      return null
    },

    applyLinkBlotUpdate(blot, newUrl, newLinkText, originalUrl) {
      const blotIndex = this.editor.getIndex(blot)
      const blotLength = blot.length()
      const currentTextOfLink = this.editor.getText(blotIndex, blotLength)

      const finalUrl = newUrl || originalUrl
      const finalText = newLinkText || currentTextOfLink

      if (finalText !== currentTextOfLink || finalUrl !== originalUrl) {
        this.editor.deleteText(blotIndex, blotLength)
        this.editor.insertText(blotIndex, finalText, 'link', finalUrl)
        this.editor.setSelection(blotIndex + finalText.length, 0)
      } else if (finalUrl !== originalUrl) {
        this.editor.formatText(blotIndex, blotLength, 'link', finalUrl)
        this.editor.setSelection(blotIndex + blotLength, 0)
      } else {
        this.editor.setSelection(blotIndex + blotLength, 0)
      }
    },

    handleNewLinkCreation(index, length, newUrl, newLinkText) {
      if (!length) {
        const textToInsert = newLinkText || newUrl
        this.editor.insertText(index, textToInsert, 'link', newUrl)
        this.editor.setSelection(index + textToInsert.length, 0)
      } else {
        const currentSelectedText = this.editor.getText(index, length)
        if (newLinkText && newLinkText !== currentSelectedText) {
          this.editor.deleteText(index, length)
          this.editor.insertText(index, newLinkText, 'link', newUrl)
          this.editor.setSelection(index + newLinkText.length, 0)
        } else {
          this.editor.formatText(index, length, 'link', newUrl)
        }
      }
    },

    insertLink(linkPayload) {
      const { index, length } = this.getSelection(true)
      const newUrl = linkPayload.url || ''
      const newLinkText = linkPayload.text || ''
      const originalActionHref = linkPayload.originalHref

      // Priority 1: Image or YouTube URL (these are always new insertions at cursor)
      if (REGEXES.VALID_IMAGE_URL.test(newUrl)) {
        this.handleImageInsertion(index, newUrl)
        this.editor.focus()
        return
      }
      if (REGEXES.VALID_YOUTUBE_URL.test(newUrl)) {
        this.handleYouTubeInsertion(index, newUrl)
        this.editor.focus()
        return
      }

      // Priority 2: Editing an existing link
      if (originalActionHref) {
        const blotToUpdate = this.findLinkBlotByUrl(originalActionHref)
        if (blotToUpdate) {
          // Found the link to update
          this.applyLinkBlotUpdate(blotToUpdate, newUrl, newLinkText, originalActionHref)
        } else {
          // Link to edit was not found (e.g., deleted by other means)
          // Create a new link at the current cursor position if newUrl is provided
          if (newUrl) { // Only create if there's a URL
            this.handleNewLinkCreation(index, length, newUrl, newLinkText)
          }
        }
      } else {
        // Priority 3: Creating a new link (not image/youtube, not an edit)
        if (newUrl) { // Only create if there's a URL
          this.handleNewLinkCreation(index, length, newUrl, newLinkText)
        }
      }
      this.editor.focus()
    },

    editLink(e) {
      this.formatting.link = e
    },

    tryRemoveLinkBlot(urlToRemove) {
      const LinkBlot = Quill.import('formats/link')
      const blots = this.editor.scroll.descendants(LinkBlot)
      for (const blot of blots) {
        if (equals(path(['formats', 'link'], blot), urlToRemove)) {
          const blotIndex = this.editor.getIndex(blot)
          const blotLength = blot.length()
          if (blotLength > 0) {
            this.editor.formatText(blotIndex, blotLength, 'link', false)
            return true
          }
        }
      }
      return false
    },

    tryRemoveLinkAtSelection(urlToRemove) {
      const currentSelection = this.editor.getSelection()
      if (this.formatting.link === urlToRemove && currentSelection) {
        const selIndex = currentSelection.index || 0
        const selLength = currentSelection.length || 0
        const lengthToFormat = selLength > 0 ? selLength : 1

        this.editor.formatLine(selIndex, lengthToFormat, 'link', false)
        this.editor.formatText(selIndex, lengthToFormat, 'link', false)

        const newFormat = this.editor.getFormat(selIndex, selLength)
        return isNil(newFormat.link)
      }
      return false
    },

    finalizeLinkRemoval() {
      this.formatting.link = ''
      this.onEditorUpdate()
      this.editor.focus()
    },

    removeLink(urlToRemove) {
      const removedByBlot = this.tryRemoveLinkBlot(urlToRemove)
      if (removedByBlot) {
        this.finalizeLinkRemoval()
        return
      }

      const removedAtSelection = this.tryRemoveLinkAtSelection(urlToRemove)
      if (removedAtSelection) {
        this.finalizeLinkRemoval()
      }
    },

    getYoutubeId(url) {
      const match = url.match(REGEXES.YOUTUBE_ID_EXTRACTION)
      if (match && match[2] && match[2].length === 11) {
        return match[2]
      }
      return undefined
    },

    findSubstringStartEnd(text, word, index) {
      const start = text.indexOf(word, index - word.length)
      const end = start + word.length
      return { start, end }
    },

    findWordAt(str, pos) {
      const left = str.slice(0, pos + 1).search(REGEXES.SPACE_BEFORE_WORD)
      const right = str.slice(pos).search(REGEXES.SPACE_AFTER_WORD)
      if (right < 0) return str.slice(left)
      return str.slice(left, right + pos)
    },

    getSelection(focus = false) {
      const selection = this.editor.getSelection(focus) || {}
      return {
        index: selection.index || 0,
        length: selection.length || 0
      }
    }
  },

  created() {
    this.initEditorIds()
  },

  mounted() {
    this.initEditor()
    this.initEvents()
  }
}
</script>
